import * as React from 'react'
import {Popover, PopoverDisclosure, PopoverProvider} from '@ariakit/react'
import {type ColumnDef} from '@tanstack/react-table'

import DataTable from '@frontrow/console/src/components/common/data-table'
import {type SelectedStudentT} from './../index'
import {teacherNamesAttr, isAllCategoryMatched, isNameTeacherMatched} from './../index'
import * as style from './../sis-matches-display.module.scss'

type Props = {
  selectedStudents: Array<SelectedStudentT>
  selectStudentRow: (original: SelectedStudentT) => React.ReactElement
  districtStudentMatchAccessor: (original: SelectedStudentT) => React.ReactElement
}

export default function SisMatchesDataTable(props: Props): React.ReactElement {
  const {selectedStudents, selectStudentRow, districtStudentMatchAccessor} = props

  const columns: ColumnDef<SelectedStudentT, unknown>[] = [
    {
      id: 'student.selected',
      header: '',
      accessorFn: () => null,
      maxSize: 30,
      cell: ctx => selectStudentRow(ctx.row.original),
      enableSorting: false
    },
    {
      id: 'student.name',
      header: 'Name',
      accessorFn: () => null,
      maxSize: 200,
      cell: ctx => {
        const student = ctx.row.original
        const freckleStudent = student.studentWithSisMatches
        const fullName = `${freckleStudent.firstName} ${freckleStudent.lastName}`
        const matchedCat = student.districtStudent.matches
        const nameStyle = isAllCategoryMatched(matchedCat)
          ? style.allMatchedStudent
          : isNameTeacherMatched(matchedCat)
            ? style.nameTeacherMatchedStudent
            : ''
        return (
          <span className={nameStyle}>
            <p>{fullName}</p>
          </span>
        )
      },
      enableSorting: false
    },
    {
      id: 'student.grade',
      header: 'Grade',
      accessorFn: student => student.studentWithSisMatches.grade,
      maxSize: 50,
      enableSorting: true
    },
    {
      id: 'student.teachers',
      header: 'Teacher(s)',
      accessorFn: () => null,
      maxSize: 200,
      cell: ctx => <StudentTeachersCell student={ctx.row.original} />,
      enableSorting: true
    },
    {
      id: 'student.sisMatches',
      header: 'District CSV Matches',
      accessorFn: () => null,
      maxSize: 500,
      cell: ctx => districtStudentMatchAccessor(ctx.row.original),
      enableSorting: false
    }
  ]

  return (
    <DataTable
      columns={columns}
      updateUrlOnFilter={false}
      data={selectedStudents}
      isLoading={false}
      defaultSorted={[{id: 'student.grade', desc: false}]}
    />
  )
}

function StudentTeachersCell({student}: {student: SelectedStudentT}): React.ReactElement {
  const teachers = teacherNamesAttr(student.studentWithSisMatches.teachers)

  return student.studentWithSisMatches.teachers.length > 1 ? (
    <PopoverProvider placement="bottom">
      <p>
        Multiple teachers{' '}
        <PopoverDisclosure>
          <i className={`fa fa-question-circle ${style.teachersPopover}`} />
        </PopoverDisclosure>
        <Popover
          style={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            padding: '8px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000
          }}
        >
          {teachers}
        </Popover>
      </p>
    </PopoverProvider>
  ) : (
    <div>{teachers}</div>
  )
}
